import React, { useRef, useEffect, useState } from 'react';
import useCamera from '../../hooks/useCamera';

const CameraCapture = ({ onCapture, onCancel }) => {
  const videoRef = useRef(null);
  const photoRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const { stream, error, isCameraReady, setVideoElement } = useCamera();

  const [countdown, setCountdown] = useState(null);
  const [countdownDuration, setCountdownDuration] = useState(3);
  const [isCountingDown, setIsCountingDown] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      setVideoElement(videoRef.current);
    }
  }, [stream, setVideoElement]);

  useEffect(() => {
    if (isCountingDown) {
      setCountdown(countdownDuration);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(countdownIntervalRef.current);
            takePhoto();
            setIsCountingDown(false);
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }
    return () => clearInterval(countdownIntervalRef.current);
  }, [isCountingDown, countdownDuration]);

  const takePhoto = () => {
    const video = videoRef.current;
    const photo = photoRef.current;

    if (video && photo) {
      photo.width = video.videoWidth;
      photo.height = video.videoHeight;
      const context = photo.getContext('2d');
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

      photo.toBlob((blob) => {
        if (blob) {
          const capturedFile = new File([blob], `captured-${Date.now()}.jpg`, { type: 'image/jpeg' });
          onCapture(capturedFile);
        } else {
          console.error("Failed to create blob from canvas.");
        }
      }, 'image/jpeg');
    }
  };

  const handleStartCountdown = () => {
    if (isCameraReady) {
      setIsCountingDown(true);
    }
  };

  const handleCountdownChange = (amount) => {
    setCountdownDuration((prevDuration) => {
      const newDuration = prevDuration + amount;
      return newDuration >= 1 ? newDuration : 1;
    });
  };

  if (error) {
    return (
      <div className="text-center text-red-500 p-8">
        {error}
        <button onClick={onCancel} className="mt-4 bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition-colors duration-200">
          Go Back
        </button>
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="text-center text-gray-500 p-8">
        Initializing camera...
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full max-w-lg mb-4 aspect-video rounded-lg overflow-hidden shadow-lg">
        <video ref={videoRef} autoPlay playsInline className="absolute top-0 left-0 w-full h-full object-cover"></video>
        {isCountingDown && (
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <div className="flex items-center justify-center w-36 h-36 md:w-48 md:h-48 rounded-full bg-black bg-opacity-50 text-white text-7xl md:text-9xl font-extrabold animate-pulse">
              {countdown}
            </div>
          </div>
        )}
      </div>
      <canvas ref={photoRef} className="hidden"></canvas>

      <div className="flex flex-col items-center mt-4">
        <div className="flex items-center space-x-4 mb-4">
          <button onClick={() => handleCountdownChange(-1)} className="bg-gray-200 text-gray-800 p-2 rounded-full">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path></svg>
          </button>
          <span className="text-lg font-semibold">{countdownDuration}s</span>
          <button onClick={() => handleCountdownChange(1)} className="bg-gray-200 text-gray-800 p-2 rounded-full">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"></path></svg>
          </button>
        </div>
        
        <div className="flex space-x-4">
          <button 
            onClick={handleStartCountdown}
            disabled={!isCameraReady || isCountingDown}
            className={`px-8 py-3 rounded-full text-lg font-semibold transition-colors duration-200
              ${isCameraReady && !isCountingDown ? 'bg-black text-white hover:bg-gray-800' : 'bg-gray-400 text-gray-700 cursor-not-allowed'}`}
          >
            {isCountingDown ? `Capturing in ${countdown}...` : 'Take Photo'}
          </button>
          <button
            onClick={onCancel}
            className="bg-gray-200 text-gray-800 px-8 py-3 rounded-full text-lg font-semibold hover:bg-gray-300 transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;